/**
 * `question` — the dedicated 13th MCP tool (WP-638 S3, §4.3 of the routed-questions design
 * spec: docs/superpowers/specs/2026-08-10-routed-questions-design.md).
 *
 * `workspace` already carries 9 actions (§2); adding the eight question verbs would make 17 —
 * no per-noun ceiling in this codebase tolerates that (§4.3's own citation:
 * served-schema-shape.test.ts asserts `collections`/`map` sit exactly at the 12-action
 * ceiling). The real, already-shipped precedent for "a noun's actions would overflow" is to
 * split into a sibling tool (`chain`/`chain-review`), not overload the original — this file is
 * that split, same two-layer `z.discriminatedUnion('action', [...])` shape as the `shape` tool
 * (packages/mcp-server/src/tools/shape.ts).
 *
 * S3 ships SEVEN write actions — every one backed by a gateway route registered in the SAME
 * slice (convex/http.ts's `question.*` rows). `list` (the eighth action) ships in S4, in the
 * same vertical as its target read model (ackGateFacts.ts) — §4.1's slice-boundary fix.
 *
 * Confirmation/refusal strings quote §7.7 as closely as S3's available data allows. S3 has NO
 * read model yet (that ships in S4) — the write verbs (convex/questions/routingFacet.ts /
 * routingFacetVerbs.ts) were extended additively (WP-638 S3) to return the routing outcome
 * (status/suggestedRoleId/ownerRoleId/routingReason/domainSlugs/etc.) alongside `entryId` so
 * this tool can render an accurate confirmation without a second round-trip to a query that
 * does not exist yet. Role/team-lead/asker NAMES are resolved with one extra `chain.getEntry`
 * lookup (a real, already-registered read route) — never a private parser.
 */

import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z } from "zod/v3";
import { thinWrapper, parseOrFail, successResult, failureResult, type ToolResult } from "../envelope.js";
import { kernelQuery, kernelMutation, requireWriteAccess, runWithToolContext } from "../client.js";
import { stripControlChars } from "../lib/strip.js";
import { dispatchDiscriminated, backpressureResult, type ActionParamSpec } from "../compound-tool.js";
import { classifyError } from "../errors.js";

// WP-638 S4 (§4.1/§4.3's slice-boundary fix): 'list', the eighth action, joins the seven S3
// shipped — same vertical as its target read model (ackGateFacts.ts / question.list route).
const QUESTION_ACTIONS = ["create", "adopt", "assign", "snooze", "decline", "answer", "force-close", "list"] as const;
type QuestionAction = (typeof QUESTION_ACTIONS)[number];

// ─── §3a — REGISTERED layer: flat ZodObject, action enum + superset of params ──────────────
export const questionSchema = z.object({
  action: z.enum(QUESTION_ACTIONS).describe(
    "'create': create a new routed question from text (domains come from `pb question --help` / this tool's own refusal — never hardcode a slug). " +
      "'adopt': attach routing to a tension already captured (e.g. via `pb capture \"TEN: ...\"`) — the pilot's real birth path; without adopt, captured tensions stay invisible to routing forever. " +
      "'assign': confirm ('take it', omit toRole/toPerson) or reassign (supply toRole and/or toPerson) ownership. " +
      "'snooze': defer a routed/escalated question (max 2, then it escalates). " +
      "'decline': pass on a suggested question, or (as owner/lead) decline an owned one — reason required. " +
      "'answer': close the loop — creates the answering entry itself unless --entry is given. " +
      "'force-close': Team-Lead-only escape hatch — reason required. " +
      "'list': the ack-gate read model — what you owe (or, with mine:true, every question you asked, including closed ones).",
  ),
  entryId: z.string().max(200).optional().describe("Target question's id (human TEN-nnn form or a raw entry id). Required for every action except 'create'/'list'."),
  text: z.string().max(5000).optional().describe("'create': the question text. 'answer': the answer text (mutually exclusive with answerEntryId, max 2000 chars)."),
  domain: z.string().max(200).optional().describe("'create'/'adopt': the domain slug (see this tool's refusal for the live list)."),
  blocks: z.array(z.string().max(200)).max(50).optional().describe("'create'/'adopt': entry ids this question is blocking (repeatable)."),
  toRole: z.string().max(200).optional().describe("'assign': reassign to this role (human ROL-nnn form or a raw entry id)."),
  toPerson: z.string().max(200).optional().describe("'assign': reassign to this person (a raw person id)."),
  reason: z.string().max(2000).optional().describe("'decline'/'force-close': required reason."),
  answerEntryId: z.string().max(200).optional().describe("'answer': point at an already-written entry instead of supplying text (mutually exclusive with text)."),
  mine: z.boolean().optional().describe("'list': true for arm C (every question YOU asked, every status). Omit/false for the ack-gate view (what you owe)."),
});

// ─── §3b — HANDLER layer: per-action discriminatedUnion ────────────────────────────────────
const createVariant = z.object({ action: z.literal("create"), text: z.string().min(1).max(5000), domain: z.string().max(200).optional(), blocks: z.array(z.string().max(200)).max(50).optional() });
const adoptVariant = z.object({ action: z.literal("adopt"), entryId: z.string().max(200), domain: z.string().max(200).optional(), blocks: z.array(z.string().max(200)).max(50).optional() });
const assignVariant = z.object({ action: z.literal("assign"), entryId: z.string().max(200), toRole: z.string().max(200).optional(), toPerson: z.string().max(200).optional() });
const snoozeVariant = z.object({ action: z.literal("snooze"), entryId: z.string().max(200) });
const declineVariant = z.object({ action: z.literal("decline"), entryId: z.string().max(200), reason: z.string().min(1).max(2000) });
// `text`/`answerEntryId` are both optional here — Zod's discriminatedUnion requires every
// member to be a plain ZodObject (this file's own `questionActionUnion` type, and
// compound-tool.ts's `ZodDiscriminatedUnion<"action", readonly [z.ZodObject<any>, ...]>` pin
// it) so it can read the literal discriminator off `.shape`. A `.refine()` here would turn
// this into a ZodEffects, which is not a ZodObject and cannot be a union member — Zod cannot
// express "exactly one of text/answerEntryId" at this layer. The "both or neither" case is
// therefore checked explicitly in handleQuestionAnswer via the same backpressureResult()
// channel dispatchDiscriminated uses for every other wrong-param-combination failure.
const answerVariant = z.object({ action: z.literal("answer"), entryId: z.string().max(200), text: z.string().max(2000).optional(), answerEntryId: z.string().max(200).optional() });
const forceCloseVariant = z.object({ action: z.literal("force-close"), entryId: z.string().max(200), reason: z.string().min(1).max(2000) });
const listVariant = z.object({ action: z.literal("list"), mine: z.boolean().optional() });

export const questionActionUnion = z.discriminatedUnion("action", [
  createVariant, adoptVariant, assignVariant, snoozeVariant, declineVariant, answerVariant, forceCloseVariant, listVariant,
]);

export const QUESTION_ACTION_SPECS: Record<QuestionAction, ActionParamSpec> = {
  create: { params: ["text", "domain", "blocks"], description: "text is required; domain/blocks are optional." },
  adopt: { params: ["entryId", "domain", "blocks"], description: "entryId is required (the already-captured tension); domain/blocks are optional." },
  assign: { params: ["entryId", "toRole", "toPerson"], description: "entryId is required; omit toRole/toPerson to self-confirm, or supply one/both to reassign." },
  snooze: { params: ["entryId"], description: "entryId is required." },
  decline: { params: ["entryId", "reason"], description: "entryId and reason are both required." },
  answer: { params: ["entryId", "text", "answerEntryId"], description: "entryId is required; supply exactly one of text or answerEntryId." },
  "force-close": { params: ["entryId", "reason"], description: "entryId and reason are both required." },
  list: { params: ["mine"], description: "mine is optional — omit/false for the ack-gate view, true for every question you asked." },
};

// ─── Human-id resolution (R43) ──────────────────────────────────────────────────────────────
// The question write verbs declare entryId/toRoleId as v.id('entries') — a raw Convex doc id,
// never a string parser of their own. `chain.getEntry` is the real, already-registered
// gateway route that resolves either form (STD-102) — this tool routes through it instead of
// inventing a private parser, satisfying R43 without touching the verbs' argument shapes.
const HUMAN_ID_PATTERN = /^[A-Za-z]+-\d+$/;

async function resolveEntryRef(raw: string, label: string): Promise<string> {
  if (!HUMAN_ID_PATTERN.test(raw)) return raw;
  const entry = await kernelQuery<{ _id: string } | null>("chain.getEntry", { entryId: raw });
  if (!entry) throw new Error(`${label} '${raw}' not found.`);
  return entry._id;
}

// `chain.getEntry`'s `id` param is `v.id('entries')` — it can resolve a role/domain id (both
// live in `entries`) but NOT a `people`-table id (e.g. `askerPersonId`, `ownerPersonId`), which
// throws an ArgumentValidationError. Caught here so a name-resolution failure (wrong table, or
// any other lookup miss) never breaks the write confirmation it decorates — degrades to
// undefined, never a crash. Bug found live (S3 build's own live-proof run against the seeded
// preview): the first `answer` proof call surfaced exactly this cross-table mismatch.
async function displayName(id: string | undefined): Promise<string | undefined> {
  if (!id) return undefined;
  try {
    const entry = await kernelQuery<{ name?: string } | null>("chain.getEntry", { id });
    // Late-merge follow-up (PR #635 review, PRRT_kwDORS4ils6YuypX, P1): every caller of this
    // helper interpolates the result straight into MCP text content — a single choke point here
    // covers every role/lead name confirmation in this file at once.
    return entry?.name ? stripControlChars(entry.name) : undefined;
  } catch {
    return undefined;
  }
}

function domainListSuffix(domainSlugs?: string[]): string {
  if (!domainSlugs) return "";
  return domainSlugs.length > 0 ? ` Domains in this workspace: ${domainSlugs.join(", ")}.` : " No domains exist in this workspace yet.";
}

// ─── Action handlers ─────────────────────────────────────────────────────────────────────────

interface CreateAdoptResult {
  entryId: string;
  facetId: string;
  status: "unrouted" | "escalated";
  suggestedRoleId?: string;
  ownerRoleId?: string;
  routingReason: string;
  domainSlugs: string[];
  nudge?: string;
}

async function renderCreateAdoptConfirmation(result: CreateAdoptResult, isAdopt: boolean): Promise<string> {
  const lead = isAdopt ? `${result.entryId} is now a routed question,` : "Question created and";
  let base: string;
  if (result.status === "unrouted") {
    const roleName = (await displayName(result.suggestedRoleId)) ?? result.suggestedRoleId;
    base = isAdopt ? `${lead} suggested to ${roleName}.` : `${lead} suggested to ${roleName}. Confirm with: pb question assign ${result.entryId}`;
  } else {
    const leadName = (await displayName(result.ownerRoleId)) ?? result.ownerRoleId;
    base = isAdopt
      ? `${result.entryId} is now a routed question. Nothing here says who owns it, so it has gone to ${leadName}.`
      : `Question created. Nothing here says who owns it, so it has gone to ${leadName}.`;
  }
  const blocksHint = " If something is blocked on this, add blocks:[<entry-id>...].";
  const nudge = result.nudge ? ` ${result.nudge}` : "";
  return `${base}${domainListSuffix(result.domainSlugs)}${blocksHint}${nudge}`;
}

export async function handleQuestionCreate(text: string, domain?: string, blocks?: string[]): Promise<ToolResult> {
  requireWriteAccess();
  try {
    const resolvedBlocks = blocks ? await Promise.all(blocks.map((b) => resolveEntryRef(b, "Blocked entry"))) : undefined;
    const result = await kernelMutation<CreateAdoptResult>("question.create", { text, domainSlug: domain, blocks: resolvedBlocks });
    const message = await renderCreateAdoptConfirmation(result, false);
    // PRRT_kwDORS4ils6YnqKm (round 2): bare `assign` is legal ONLY from 'unrouted' (§3.9b) —
    // a born-'escalated' result (no domain, no suggestable role, ...) has no suggestion to
    // confirm, and following this next-action would hit "already owned ... use --to-role/
    // --to-person to reassign." Offer it only when a suggestion actually exists.
    const next = result.status === "unrouted"
      ? [{ tool: "question", description: "Confirm or reassign the suggestion", parameters: { action: "assign", entryId: result.entryId } }]
      : undefined;
    return successResult(message, message, result, next);
  } catch (err) {
    return questionErrorResult(err, "create", { text, domain, blocks });
  }
}

export async function handleQuestionAdopt(entryId: string, domain?: string, blocks?: string[]): Promise<ToolResult> {
  requireWriteAccess();
  try {
    const resolvedEntryId = await resolveEntryRef(entryId, "Entry");
    const resolvedBlocks = blocks ? await Promise.all(blocks.map((b) => resolveEntryRef(b, "Blocked entry"))) : undefined;
    const result = await kernelMutation<CreateAdoptResult>("question.adopt", { entryId: resolvedEntryId, domainSlug: domain, blocks: resolvedBlocks });
    const message = await renderCreateAdoptConfirmation(result, true);
    return successResult(message, message, result);
  } catch (err) {
    return questionErrorResult(err, "adopt", { entryId, domain, blocks });
  }
}

interface AssignResult { entryId: string; status: "routed" | "unrouted" | "escalated" | "processed"; ownerRoleId?: string; ownerPersonId?: string; ownerPersonName?: string }

export async function handleQuestionAssign(entryId: string, toRole?: string, toPerson?: string): Promise<ToolResult> {
  requireWriteAccess();
  try {
    const resolvedEntryId = await resolveEntryRef(entryId, "Entry");
    const resolvedToRoleId = toRole ? await resolveEntryRef(toRole, "Role") : undefined;
    const result = await kernelMutation<AssignResult>("question.assign", { entryId: resolvedEntryId, toRoleId: resolvedToRoleId, toPersonId: toPerson });
    const isReassign = Boolean(toRole || toPerson);
    let message: string;
    if (isReassign) {
      // PRRT_kwDORS4ils6YnqKM (round 2): people are a separate table from entries —
      // displayName()/chain.getEntry cannot resolve a personId. Prefer the mutation's own
      // server-resolved ownerPersonName (a plain ctx.db.get, done where it can succeed) when
      // the caller named a person; only fall back to the role-name lookup otherwise.
      // Late-merge follow-up (PR #635 review, PRRT_kwDORS4ils6YuypX, P1): ownerPersonName is
      // server-resolved directly (ctx.db.get), never through displayName()'s own sanitizing
      // choke point above — sanitized here at the point of use.
      const who = toPerson ? (stripControlChars(result.ownerPersonName ?? "") || result.ownerPersonId || "the target") : ((await displayName(result.ownerRoleId)) ?? result.ownerRoleId ?? "the target");
      message = `${entryId} reassigned to ${who}. They'll see it at their next session.`;
    } else {
      message = `${entryId} is yours now.`;
    }
    return successResult(message, message, result);
  } catch (err) {
    return questionErrorResult(err, "assign", { entryId, toRole, toPerson });
  }
}

interface SnoozeResult { entryId: string; status: "routed" | "escalated"; snoozeCount?: number; snoozeUntil?: number; ownerRoleId?: string }

export async function handleQuestionSnooze(entryId: string): Promise<ToolResult> {
  requireWriteAccess();
  try {
    const resolvedEntryId = await resolveEntryRef(entryId, "Entry");
    const result = await kernelMutation<SnoozeResult>("question.snooze", { entryId: resolvedEntryId });
    let message: string;
    if (result.snoozeUntil) {
      message = `Snoozed until ${new Date(result.snoozeUntil).toISOString().slice(0, 10)}. ${result.snoozeCount ?? "?"} of 2 snoozes used.`;
    } else if (result.status === "escalated") {
      const leadName = (await displayName(result.ownerRoleId)) ?? "the team lead";
      message = `You have used both snoozes on this question — it has been escalated to ${leadName}.`;
    } else {
      message = "No snoozes left — answer it or force-close it.";
    }
    return successResult(message, message, result);
  } catch (err) {
    return questionErrorResult(err, "snooze", { entryId });
  }
}

interface DeclineResult { entryId: string; status: "unrouted" | "escalated" | "processed"; suggestedRoleId?: string; ownerRoleId?: string; dependentsCount?: number; isRoot?: boolean }

export async function handleQuestionDecline(entryId: string, reason: string): Promise<ToolResult> {
  requireWriteAccess();
  try {
    const resolvedEntryId = await resolveEntryRef(entryId, "Entry");
    const result = await kernelMutation<DeclineResult>("question.decline", { entryId: resolvedEntryId, reason });
    // `isRoot` disambiguates two branches sharing the identical {status:'escalated',
    // ownerRoleId} shape: a suggestee's PASS (§3.7a, never sets isRoot) vs an owner/lead's
    // decline (§3.7b, always sets it explicitly — true at the root, false below it).
    let message: string;
    if (result.status === "processed") {
      message = "Declined and closed.";
    } else if (result.isRoot === true) {
      message = `Declined — but ${result.dependentsCount ?? 0} item(s) still depend on it, so it stays with you. Answer it or force-close it.`;
    } else if (result.isRoot === false) {
      const leadName = (await displayName(result.ownerRoleId)) ?? result.ownerRoleId;
      message = `Declined and escalated to ${leadName}.`;
    } else if (result.suggestedRoleId) {
      const roleName = (await displayName(result.suggestedRoleId)) ?? result.suggestedRoleId;
      message = `Passed. ${entryId} is now suggested to ${roleName}.`;
    } else if (result.ownerRoleId) {
      const leadName = (await displayName(result.ownerRoleId)) ?? result.ownerRoleId;
      message = `Passed. Nobody else here owns this, so it has gone to ${leadName}.`;
    } else {
      message = "Declined.";
    }
    return successResult(message, message, result);
  } catch (err) {
    return questionErrorResult(err, "decline", { entryId, reason });
  }
}

interface AnswerResult { entryId: string; status: "processed"; answerEntryId?: string; answerPending?: boolean; askerPersonId?: string; askerName?: string; idempotent?: boolean; processedVia?: "answered" | "declined" | "force_closed" }

export async function handleQuestionAnswer(entryId: string, text?: string, answerEntryId?: string): Promise<ToolResult> {
  // Tool-boundary check for the "exactly one of text/answerEntryId" rule the answerVariant
  // schema cannot express (see that variant's own comment) — structured backpressure BEFORE
  // requireWriteAccess/any write call, never a server round-trip. §7.7's declared strings.
  if (text === undefined && answerEntryId === undefined) {
    return backpressureResult("question", "answer", QUESTION_ACTION_SPECS.answer, "Give the answer as text, or point at an entry with --entry <id>.");
  }
  if (text !== undefined && answerEntryId !== undefined) {
    return backpressureResult("question", "answer", QUESTION_ACTION_SPECS.answer, "Use either answer text or --entry, not both.");
  }
  requireWriteAccess();
  try {
    const resolvedEntryId = await resolveEntryRef(entryId, "Entry");
    const resolvedAnswerEntryId = answerEntryId ? await resolveEntryRef(answerEntryId, "Answer entry") : undefined;
    const result = await kernelMutation<AnswerResult>("question.answer", { entryId: resolvedEntryId, text, answerEntryId: resolvedAnswerEntryId });
    // PRRT_kwDORS4ils6YnqKM (round 2): people are a separate table from entries — the
    // mutation already resolves the asker's display name server-side (a plain ctx.db.get);
    // no client-side chain.getEntry lookup is attempted here, since it structurally cannot
    // reach the people table.
    // Late-merge follow-up (PR #635 review, PRRT_kwDORS4ils6YuypX, P1): server-resolved,
    // same as ownerPersonName above — sanitized at the point of use.
    const askerName = (result.askerName ? stripControlChars(result.askerName) : "") || "The asker";
    const pendingNote = result.answerPending ? " The answer is a draft awaiting accept." : "";
    // S3's `list` action didn't exist yet (it's S4's own vertical, §4.1's slice-boundary fix),
    // so the confirmation couldn't point at it. Now that S4 has landed in this same PR (review
    // finding), restore §7.7's documented string (design spec line 1413).
    // Late #633 finding (PRRT_kwDORS4ils6YozQN): a question closed by decline/forceClose
    // reaches the SAME idempotent branch as a genuine re-answer — say "already answered" only
    // when it actually was; a decline/force-close reaches this same terminal `processed` state
    // via a different verb, and claiming it was "answered" is false. An older server response
    // that omits `processedVia` is treated as answered — the pre-fix behavior, so a caller that
    // has not yet threaded the new field never regresses.
    const message = result.idempotent
      ? result.processedVia === "declined" || result.processedVia === "force_closed"
        ? `${entryId} is already processed (${result.processedVia === "declined" ? "declined" : "force-closed"}) — no change.`
        : `${entryId} is already answered — no change.`
      : `Answered. ${askerName} will see the outcome at their next session (and in pb question list --mine).${pendingNote}`;
    return successResult(message, message, result);
  } catch (err) {
    return questionErrorResult(err, "answer", { entryId, text, answerEntryId });
  }
}

interface ForceCloseResult { entryId: string; status: "processed"; idempotent?: boolean }

export async function handleQuestionForceClose(entryId: string, reason: string): Promise<ToolResult> {
  requireWriteAccess();
  try {
    const resolvedEntryId = await resolveEntryRef(entryId, "Entry");
    const result = await kernelMutation<ForceCloseResult>("question.forceClose", { entryId: resolvedEntryId, reason });
    const message = result.idempotent ? `${entryId} is already closed — no change.` : "Force-closed. Reason recorded.";
    return successResult(message, message, result);
  } catch (err) {
    return questionErrorResult(err, "force-close", { entryId, reason });
  }
}

// ─── list (WP-638 S4, §4.1's slice-boundary fix) ────────────────────────────────────────────

interface QuestionListRow {
  entryId: string;
  question: string;
  status: string;
  stateLabel: string;
  routingReason?: string;
  why: string;
  domain: string;
  withLabel: string;
  ageDays: number;
  legalVerbs: string[];
  outcome?: string;
  /** §7.7's documented `dependents: { count, named }` MCP JSON field — pass-through from the
   *  real read model (convex/questions/ackGateFacts.ts), not recomputed here. */
  dependents: { count: number; truncated?: boolean; named: { name: string; id: string }[] };
}
interface ListResult { questions: QuestionListRow[]; viewerPersonId: string; truncated?: boolean }

/** §7.7's `pb question list` columns, MCP-JSON twin — `state` carries the raw enum for machine
 *  consumers, `stateLabel` the human string (§7.8: both, never a choice between parseable and
 *  readable). Empty list: `No open questions.` / `--mine`: `You haven't asked any questions yet.` */
export async function handleQuestionList(mine?: boolean): Promise<ToolResult> {
  try {
    const result = await kernelQuery<ListResult>("question.list", { mine });
    const rows = result.questions;
    if (rows.length === 0) {
      const message = mine ? "You haven't asked any questions yet." : "No open questions.";
      return successResult(message, message, { ...result, action: "list" });
    }
    // Late-merge follow-up (PR #635 review, PRRT_kwDORS4ils6YuypX, P1): every one of these
    // fields is workspace-controlled (question text, domain/role/person names via `why`/
    // `withLabel`, an outcome string) and reaches MCP text content verbatim — an MCP client
    // that prints tool output to a terminal (many do) inherits the same ANSI/control-char
    // injection risk BET-269 C3 guards on the CLI side. Same sanitizer, synced (lib/strip.ts).
    const lines = rows.map((r) => {
      const outcomeSuffix = mine && r.outcome ? ` — ${stripControlChars(r.outcome)}` : "";
      return `[${stripControlChars(r.entryId)}] ${stripControlChars(r.question)} — ${stripControlChars(r.stateLabel)} — ${stripControlChars(r.why)} — ${stripControlChars(r.domain)} — ${stripControlChars(r.withLabel)} — ${r.ageDays}d${outcomeSuffix}`;
    });
    // Review finding: the scan is honestly capped — a caller past the cap must be told the list
    // isn't exhaustive (the machine `truncated` field already carried this; only the human-readable
    // text was silent about it).
    const truncationNote = result.truncated
      ? `\n(${mine ? "showing your most recent questions only — older ones exist but are not shown" : "this list may be incomplete — some obligations may not be shown"})`
      : "";
    const message = `${rows.length} question${rows.length === 1 ? "" : "s"}:\n${lines.join("\n")}${truncationNote}`;
    return successResult(message, message, { ...result, action: "list" });
  } catch (err) {
    return questionErrorResult(err, "list", { mine });
  }
}

/** Shared error->ToolResult mapping — preserves the structured code the Core functions throw
 *  (VALIDATION_FAILED / AUTHZ_PERMISSION_DENIED / NOT_FOUND) rather than a generic 500 shape. */
function questionErrorResult(err: unknown, action: QuestionAction, params: Record<string, unknown> = {}): ToolResult {
  const classified = classifyError(err);
  // PRRT_kwDORS4ils6YnqKu (round 2): `{action}` alone is missing every OTHER required field
  // the real schema declares (e.g. `text` for create, `entryId` for snooze, `entryId`+`reason`
  // for decline) — an MCP client following it would fail the tool's own registered-layer
  // validation before retrying anything. Preserve the caller's ORIGINAL params (minus
  // undefined values, so an omitted optional field doesn't get advertised as required).
  const definedParams = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined));
  // Late #633 finding (PRRT_kwDORS4ils6YozQI): a VALIDATION_ERROR is a deterministic refusal —
  // the same arguments against the same server/facet state fail identically (an exhausted
  // snooze, an unknown domain slug), so offering "retry unchanged" as availableActions would
  // let an MCP agent loop on it forever. Omit the unchanged-retry fallback for this one code;
  // every other unclassified code here (BACKEND_UNAVAILABLE, GATEWAY_TIMEOUT, rate-limited's own
  // recovery text, a genuinely unmapped convexCode) can legitimately succeed on a bare retry.
  const fallbackActions = classified.code === "VALIDATION_ERROR"
    ? undefined
    : [{ tool: "question", description: `Retry ${action}`, parameters: { action, ...definedParams } }];
  return failureResult(
    classified.message,
    classified.code,
    classified.message,
    classified.recovery ?? "Use action='create' with no --domain, or `pb question --help` / this tool's description, to find valid domains and next steps.",
    classified.availableActions ?? fallbackActions,
    classified.diagnostics as Record<string, unknown> | undefined,
  );
}

export function registerQuestionTools(server: McpServer): void {
  const questionHandlers: Record<QuestionAction, (data: any) => Promise<ToolResult>> = {
    create: (data) => handleQuestionCreate(data.text, data.domain, data.blocks),
    adopt: (data) => handleQuestionAdopt(data.entryId, data.domain, data.blocks),
    assign: (data) => handleQuestionAssign(data.entryId, data.toRole, data.toPerson),
    snooze: (data) => handleQuestionSnooze(data.entryId),
    decline: (data) => handleQuestionDecline(data.entryId, data.reason),
    answer: (data) => handleQuestionAnswer(data.entryId, data.text, data.answerEntryId),
    "force-close": (data) => handleQuestionForceClose(data.entryId, data.reason),
    list: (data) => handleQuestionList(data.mine),
  };

  server.registerTool(
    "question",
    {
      title: "Question",
      description:
        "Routed questions — a question with no owner routes itself to the owning role, the team lead when ambiguous, and up the team-lead chain until someone owns it. Eight actions:\n\n" +
        "- **create**: create a new routed question from text. Domains are discovered by using this tool — an unresolvable `domain` refusal lists the workspace's real slugs; there is no fixed list to memorize.\n" +
        "- **adopt**: attach routing to a tension already captured (e.g. via `capture` with `collection: tensions`) — the real birth path for most questions. Without adopt, a captured tension stays invisible to routing forever.\n" +
        "- **assign**: confirm ('take it', no `toRole`/`toPerson`) or reassign (`toRole`/`toPerson`) ownership.\n" +
        "- **snooze**: defer a routed/escalated question — max 2, then it escalates to the team lead.\n" +
        "- **decline**: pass on a suggested question (never closes it), or — as the owner/team lead — decline an owned one (reason required; closes it, or escalates it, depending on dependents).\n" +
        "- **answer**: close the loop. Creates the answering entry itself from `text`, or points at an existing one via `answerEntryId` — never both.\n" +
        "- **force-close**: Team-Lead-only escape hatch (reason required) for a question that cannot otherwise be resolved.\n" +
        "- **list**: the ack-gate read model — what you owe (`mine:false`, the default), or (`mine:true`) every question you asked, including closed ones, with its outcome.\n\n" +
        "`blocks` (repeatable, on create/adopt) records what a question is holding up — pass entry ids it blocks.",
      inputSchema: questionSchema,
      annotations: { readOnlyHint: false, idempotentHint: false, openWorldHint: false },
    },
    thinWrapper(async (args) => {
      const parsed = parseOrFail(questionSchema, args);
      if (!parsed.ok) return parsed.result;
      const { action } = parsed.data;

      return runWithToolContext({ tool: "question", action }, () =>
        dispatchDiscriminated("question", questionActionUnion, parsed.data, QUESTION_ACTION_SPECS, questionHandlers),
      );
    }),
  );
}
